import React from "react";
import { BarChart2 } from "lucide-react";
import type { LocationPerf } from "../../types/dashboard.types";

const fmt = (n: number) => n >= 1_000 ? `${(n / 1_000).toFixed(1)}k` : String(n);

interface RowProps {
  rank: number;
  title: string;
  impressions: number;
  calls: number;
  max: number;
}

const Row: React.FC<RowProps> = ({ rank, title, impressions, calls, max }) => (
  <div className="flex items-center gap-3 py-2.5 border-b border-slate-50 last:border-0">
    <span className="w-5 h-5 rounded-full bg-slate-100 text-[10px] font-bold text-slate-500 flex items-center justify-center flex-shrink-0">
      {rank}
    </span>
    <div className="flex-1 min-w-0">
      <p className="text-[12px] font-semibold text-slate-700 truncate">{title}</p>
      <div className="mt-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
        <div
          className="h-full rounded-full bg-gradient-to-r from-[#0F52BA] to-blue-400"
          style={{ width: `${max > 0 ? Math.round((impressions / max) * 100) : 0}%` }}
        />
      </div>
    </div>
    <div className="text-right flex-shrink-0">
      <p className="text-[12px] font-bold text-slate-700">{fmt(impressions)}</p>
      <p className="text-[10px] text-slate-400">{calls} appels</p>
    </div>
  </div>
);

interface Props {
  locations: LocationPerf[];
}

const TopFichesCard: React.FC<Props> = ({ locations }) => {
  const top  = [...locations].filter(l => !l.error)
    .sort((a, b) => (b.kpis?.totalImpressions ?? 0) - (a.kpis?.totalImpressions ?? 0))
    .slice(0, 5);
  const max  = top[0]?.kpis?.totalImpressions ?? 1;

  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-[13px] font-bold text-slate-700">Top fiches</h3>
          <p className="text-[11px] text-slate-400">Par impressions</p>
        </div>
        <BarChart2 size={15} className="text-slate-300" />
      </div>

      {top.length > 0 ? top.map((loc, i) => (
        <Row
          key={loc.locationName}
          rank={i + 1}
          title={loc.title ?? loc.locationName}
          impressions={loc.kpis?.totalImpressions ?? 0}
          calls={loc.kpis?.callClicks ?? 0}
          max={max}
        />
      )) : (
        <p className="text-xs text-slate-300 text-center py-8">Aucune fiche</p>
      )}
    </div>
  );
};

export default TopFichesCard;